import { useState } from "react";
import { Eye, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAtomValue } from "jotai";
import { Button } from "@/components/ui/Button";
import { useAuthStore } from "@/store/auth";
import { impersonationContextAtom } from "@/store/impersonation";
import { useExitImpersonation } from "@/features/admin/hooks/useExitImpersonation";

/**
 * Full-screen state shown when a superadmin tries to reach an `/admin/*` (or
 * `/dealer/*`) route while an impersonation session is active (TASK-3677).
 *
 * Replaces the previous SILENT redirect (the active identity during
 * impersonation is the tenant `company_admin`, so the role guards used to bounce
 * the user to `/` with no explanation). Instead this names the impersonated
 * tenant and offers a single, clear exit action that ends impersonation and
 * returns to the admin panel.
 */
export function ImpersonationBlocked() {
  const { t } = useTranslation();
  const context = useAtomValue(impersonationContextAtom);
  const tenantName = useAuthStore((s) => s.user?.tenant_name ?? "");
  const exitImpersonation = useExitImpersonation();
  const [exiting, setExiting] = useState(false);

  // Sprint 139: name the correct target — a dealer-target session (a superadmin
  // who reached an /admin route while impersonating a dealer) shows the dealer
  // name; a tenant-target session shows the tenant name.
  const isDealerTarget = context?.targetKind === "dealer";
  const targetName = context?.displayName ?? tenantName;
  const description = isDealerTarget
    ? t("adminPanel.impersonationBlocked.descriptionDealer", {
        name: targetName,
      })
    : t("adminPanel.impersonationBlocked.description", {
        tenantName: targetName,
      });

  async function handleExit() {
    setExiting(true);
    try {
      await exitImpersonation();
    } finally {
      setExiting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-md rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
          <Eye className="h-7 w-7 text-amber-700" aria-hidden="true" />
        </div>
        <h1 className="mb-2 text-lg font-bold text-gray-900">
          {t("adminPanel.impersonationBlocked.title")}
        </h1>
        <p className="mb-6 text-sm text-gray-500">{description}</p>
        <Button
          variant="primary"
          onClick={() => void handleExit()}
          disabled={exiting}
        >
          <LogOut size={16} className="mr-2" />
          {t("adminPanel.impersonationBlocked.exitAction")}
        </Button>
      </div>
    </div>
  );
}
